import * as React from 'react';
import { ReactNode } from 'react';
import Autocomplete, {
  AutocompleteProps,
  AutocompleteRenderGroupParams,
} from '@mui/material/Autocomplete';
import { Box, ListSubheader, Paper, TextField, TextFieldProps } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { useTranslation } from 'mir-utils/i18n';
import ListboxComponent from './ListboxComponent';

type Props = {
  virtual?: boolean;
  placeholder?: string;
  helperText?: string;
  name: string;
  label?: string;
  options?: any[];
  noOptionsText?: ReactNode;
  textFieldProps?: TextFieldProps;
};

const renderGroup = (params: AutocompleteRenderGroupParams) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

/**
 * @summary Кастомизированный компонент Select от material-ui.
 *
 * @param virtual - Если передаётся, то выпадающий список будет
 * виртуальным.
 * @param placeholder - Текст-заглушка в поле ввода.
 * @param helperText - Подсказка под полем ввода.
 * @param name - Name, который нужен для formik-а. Также передаётся в input.
 * @param label - Заголовок для поля ввода.
 * @param options - Элементы, которые будут представлены в выпадающем списке.
 * @param noOptionsText - Текс-подсказка при отсутствии элементов.
 *
 * Остальные свойства являются свойствами для Autocomplete и будут передаваться
 * согласно API.
 *
 * @see https://material-ui.com/ru/api/autocomplete/
 */
export const Select: React.FC<
  Props & Partial<AutocompleteProps<any, boolean, boolean, boolean>>
> = ({
  name,
  multiple,
  renderTags,
  virtual,
  options,
  noOptionsText,
  label,
  placeholder,
  helperText,
  textFieldProps,
  ...rest
}) => {
  const { setFieldValue, initialValues } = useFormikContext<any>();
  const { t } = useTranslation();

  return (
    <Box maxWidth="328px">
      <Field name={name}>
        {({ field, meta }: FieldProps) => (
          <Autocomplete
            {...(multiple && {
              multiple: true,
              renderTags: renderTags,
            })}
            {...(virtual && {
              disableListWrap: true,
              ListboxComponent: ListboxComponent as React.ComponentType<
                React.HTMLAttributes<HTMLElement>
              >,
              renderGroup,
            })}
            {...(initialValues?.[name] &&
              multiple && {
                defaultValue: initialValues[name],
                clearOnBlur: false,
              })}
            noOptionsText={noOptionsText || t('No elements')}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                error={meta.touched && Boolean(meta.error)}
                size="small"
                variant="outlined"
                label={label}
                helperText={meta.touched && meta.error ? meta.error : helperText}
                placeholder={placeholder}
                fullWidth
                {...textFieldProps}
              />
            )}
            PaperComponent={(props) => <Paper elevation={6} style={{ marginTop: 0 }} {...props} />}
            value={field.value}
            onChange={(_event, value) => setFieldValue(field.name, value)}
            {...rest}
          />
        )}
      </Field>
    </Box>
  );
};
